import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { authFetch } from '../auth/AuthContext';

const LOAN_TYPE_GROUPS = [
  ['Residential / bridge', ['Fix & Flip','Ground-Up Construction','Bridge','DSCR']],
  ['Land',                 ['Land','Agricultural']],
  ['Commercial',           ['Commercial','SBA 7(a)','SBA 504','Working capital / LOC','Portfolio / Blanket']],
  ['Manufactured',         ['MH Chattel','MH Land/Home']],
  ['Other',                ['2nd Mortgage','Gap / Mezzanine','Other']],
];
const LOAN_TYPES = LOAN_TYPE_GROUPS.reduce((a, g) => a.concat(g[1]), []);
const PROP_TYPES = ['SFR','Condo / Townhome','2-4 Units','Multifamily 5+','Mixed-Use','Commercial','Land / Vacant Lot','Agricultural','Manufactured / Mobile','Other'];
const LTV_BASIS = ['','Purchase price','As-is value','As-completed value','Lower of purchase-or-appraisal'];
const LTC_INCLUDES = ['','Purchase + rehab','Purchase only','All-in incl carry+closing'];
const REHAB_DELIVERY = ['','Draws-reimbursed','Funded upfront','N/A'];
const YESNO_CBC = ['','Yes','No','Case-by-case'];
const ASSIGN_BASIS = ['','% of loan','Max $ cap','Flat $'];
const ENTITY = ['','Entity required','Individual OK','Both'];
const OWNER_OCC = ['','Yes','No'];
const BROKER = ['','Yes','No','N/A'];
const AE_QUALITY = ['','Great','Responsive','Slow','Avoid'];
const SUB_METHOD = ['','Email','Portal','Both'];
const APPRAISAL_TYPE = ['','Full','Desktop','Drive-by','BPO','AVM','Other'];
const RECOURSE_TYPES = ['','Full recourse','Non-recourse','Both'];
const SITUATIONS = ['Auction / courthouse-step','REO / bank-owned','Foreclosure bailout','Short sale','Tax-lien / tax-deed','Probate / estate / inherited','Trust-held (rev/irrev)','Partner buyout','Cash-out business purpose','Delayed financing','Seller 2nds / land contracts','Wholetail'];
const LIEN_POSITIONS_FUNDED = ['1st lien','2nd lien','3rd / subordinate'];
const ASSET_CLASSES = ['Cannabis','Senior/assisted living','Hotels/hospitality','Self-storage','Mobile/manufactured home parks','Religious / faith-based','Gas station / C-store','Automotive (car wash / repair / dealership)','Restaurant / bar / nightlife','Daycare / education','Condo conversion','Industrial/warehouse','Retail','Office','Medical/healthcare','Special-purpose','Non-warrantable condos','Short-term/Airbnb','Co-living','Condotels','Student housing','RV parks','Cold storage','Distribution center','Flex space','Wineries','Marinas','Campgrounds','Funeral homes','Event venues','Parking structures','Gym / fitness'];
const CO_LIVING_TYPES = ['Standard roommates','Sober living','Group home',"Women's / DV shelter",'Supportive / transitional'];
const STATES = ['AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA','KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ','NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT','VA','WA','WV','WI','WY','DC'];


const YN3 = ['', 'Yes', 'No', 'Case by case'];
const VEST = ['', 'Entity required', 'Individual OK', 'Both'];
const PREPAY = ['', 'None', 'Step-down', 'Yield maintenance', 'Case by case'];
const APPR_T = ['', 'Full', 'Desktop', 'Drive-by', 'BPO', 'AVM'];
const COMP_BY = ['', 'Borrower', 'Lender (YSP)', 'Both'];
const RECOURSE3 = ['', 'Full recourse', 'Non-recourse', 'Both'];
const CREDIT_PULL = ['', 'Soft', 'Hard', 'None'];

// Every lender is answered against exactly these lines.
// [label, draft key, control, options]  control: text | select | chips | states
const LENDER_BLOCKS = [
  ['Terms and pricing', [
    ['Lender points', 'lenderPoints', 'text'],
    ['Broker points', 'brokerComp', 'text'],
    ['YSP allowed', 'ysp', 'select', YN3],
    ['Comp paid by', 'compPaidBy', 'select', COMP_BY],
    ['Reserves', 'reservesRequired', 'text'],
    ['Lien positions', 'lienPositions', 'chips', LIEN_POSITIONS_FUNDED],
    ['Cross-collateral', 'crossCollateral', 'select', YESNO_CBC],
    ['Wholesale allowed', 'wholesale', 'select', YESNO_CBC],
    ['Assignment fee', 'assignFeeAmount', 'text'],
    ['Assignment fee basis', 'assignFeeBasis', 'select', ASSIGN_BASIS],
    ['Other fees', 'otherFees', 'text'],
  ]],
  ['Borrower requirements', [
    ['Vesting', 'entity', 'select', VEST],
    ['Recourse', 'recourseType', 'select', RECOURSE3],
    ['Personal guarantee', 'personalGuarantee', 'select', YESNO_CBC],
    ['Foreign national', 'foreignNational', 'select', YN3],
    ['ITIN', 'itin', 'select', YN3],
    ['Non-permanent resident', 'nonPermResident', 'select', YN3],
    ['Bankruptcy seasoning', 'bankruptcySeasoning', 'text'],
    ['Foreclosure seasoning', 'foreclosureSeasoning', 'text'],
    ['Late mortgage allowed', 'lateMortgage', 'text'],
  ]],
  ['Property requirements', [
    ['Owner-occupied', 'ownerOcc', 'select', OWNER_OCC],
    ['Rural', 'ruralAllowed', 'select', YESNO_CBC],
    ['Maximum acreage', 'maxAcreage', 'text'],
    ['Appraisal required', 'appraisalRequired', 'select', YESNO_CBC],
    ['Appraisal type', 'appraisalType', 'select', APPR_T],
    ['Location requirements', 'locationReq', 'text'],
    ['Minimum property value', 'minPropValue', 'text'],
    ['Will not lend on', 'willNotLendOn', 'text'],
  ]],
  ['Process and documents', [
    ['Full application', 'fullApplication', 'select', YESNO_CBC],
    ['Credit pull', 'creditPull', 'select', CREDIT_PULL],
    ['Background check', 'backgroundCheck', 'select', YESNO_CBC],
    ['Documents', 'documentsRequired', 'text'],
  ]],
];

const PROGRAM_LINES = [
  ['Loan type', 'loanType', 'select', LOAN_TYPES],
  ['Min FICO', 'minFico', 'text'],
  ['Max LTV (purchase)', 'maxLtvPurchase', 'text'],
  ['Max LTV (refi)', 'maxLtvRefi', 'text'],
  ['Max LTP', 'maxLtp', 'text'],
  ['Max LTC', '__ltc', 'text'],
  ['Max ARV %', 'maxArv', 'text'],
  ['Min DSCR', 'minDscr', 'text'],
  ['Loan', '__loansize', 'text'],
  ['Rate', '__rate', 'text'],
  ['Points', 'points', 'text'],
  ['Term', 'term', 'text'],
  ['Interest only', 'interestOnly', 'select', YN3],
  ['Experience', 'experienceMin', 'text'],
  ['Prepay', 'prepayPenalty', 'text'],
  ['Close', 'closingSpeed', 'text'],
  ['Draws', 'rehabDelivery', 'text'],
  ['Reserves', 'reserves', 'text'],
  ['Property', 'propertyType', 'chips', PROP_TYPES],
  ['Notes', 'programNote', 'area'],
];
function programLineLabel(p, label, key) {
  if (key === 'maxLtvRefi' && String(p.loanType || '').indexOf('2nd') === 0) return 'Max CLTV';
  return label;
}

function lineValue(o, key) {
  if (!o) return '';
  const v = o[key];
  if (Array.isArray(v)) return v.join(', ');
  if (v === 0) return '0';
  return v == null ? '' : String(v);
}

function progPurchase(p) {
  const b = [];
  if (p.maxLtvPurchase != null) b.push('up to ' + p.maxLtvPurchase + '%');
  if (p.maxLtc != null) b.push('LTC ' + p.maxLtc + '%' + (p.ltcIncludes ? ' (' + p.ltcIncludes + ')' : ''));
  if (p.maxArv != null) b.push('max ' + p.maxArv + '% ARV');
  if (p.ltvBasis) b.push(String(p.ltvBasis).toLowerCase() + ' basis');
  return b.join(', ');
}
function progLtc(p) {
  if (p.maxLtc == null) return '';
  return String(p.maxLtc) + '%' + (p.ltcIncludes ? ' (' + p.ltcIncludes + ')' : '');
}
function progRefi(p) {
  if (p.maxLtvRefi == null) return '';
  return 'rate/term up to ' + p.maxLtvRefi + '%';
}
function progLoanSize(p) {
  if (p.minLoan == null && p.maxLoan == null) return '';
  return fmtRange(p.minLoan, p.maxLoan);
}
function progRate(p) {
  if (p.rateMin == null && p.rateMax == null) return '';
  return [p.rateMin, p.rateMax].filter((x) => x != null).map((x) => x + '%').join(' to ');
}
function programLineValue(p, key) {
  if (key === '__purchase') return progPurchase(p);
  if (key === '__refi') return progRefi(p);
  if (key === '__ltc') return progLtc(p);
  if (key === '__loansize') return progLoanSize(p);
  if (key === '__rate') return progRate(p);
  return lineValue(p, key);
}

const s = {
  wrap: { display: 'flex', gap: '14px', alignItems: 'flex-start' },
  listCard: { width: '230px', flexShrink: 0, background: '#fff', border: '1px solid #C9D9EE', borderRadius: '12px', overflow: 'hidden', position: 'sticky', top: '12px', boxShadow: '0 1px 3px rgba(20,60,120,0.06)' },
  searchWrap: { padding: '10px', borderBottom: '1px solid #EEF1F5' },
  searchBox: { display: 'flex', alignItems: 'center', gap: '6px', background: '#F0F5FB', border: '1px solid #D9E5F4', borderRadius: '8px', padding: '6px 9px' },
  searchInput: { border: 'none', background: 'transparent', outline: 'none', fontSize: '12px', width: '100%', fontFamily: 'inherit', color: '#0D2545' },
  countLine: { fontSize: '11px', color: '#8a9ab5', padding: '8px 11px', borderBottom: '1px solid #EEF1F5' },
  rollWrap: { padding: '9px 11px 4px', borderBottom: '1px solid #EEF1F5' },
  rollLine: { fontSize: '10.5px', color: '#5a6b85', fontWeight: 600 },
  rollBar: { display: 'flex', height: '6px', borderRadius: '5px', overflow: 'hidden', background: '#EEF1F5', marginTop: '6px' },
  vfilter: { display: 'flex', gap: '4px', padding: '8px 8px 6px', borderBottom: '1px solid #EEF1F5', flexWrap: 'wrap' },
  vtab: (on) => ({ flex: 1, fontSize: '9.5px', fontWeight: 700, padding: '5px 3px', borderRadius: '6px', border: '1px solid ' + (on ? '#BCD2EC' : '#E6EAF0'), background: on ? '#E4EDF8' : '#fff', color: on ? '#154D97' : '#5A6B82', cursor: 'pointer', fontFamily: 'inherit', textAlign: 'center', whiteSpace: 'nowrap' }),
  sdot: (c) => ({ width: '9px', height: '9px', borderRadius: '50%', background: c, flexShrink: 0, display: 'inline-block' }),
  spct: { fontSize: '10px', color: '#8a9ab5' },
  cardStatusWrap: { background: '#F0F5FB', border: '1px solid #D9E5F4', borderRadius: '9px', padding: '11px 13px', margin: '10px 0 14px' },
  cardStatusTop: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px', marginBottom: '8px' },
  statusPill: (st) => ({ fontSize: '10px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', padding: '3px 9px', borderRadius: '6px', background: st === 'verified' ? '#EAF3DE' : st === 'partial' ? '#FAEEDA' : '#F0F5FB', color: st === 'verified' ? '#3B6D11' : st === 'partial' ? '#8a5a12' : '#1A5FA8' }),
  compTrack: { height: '7px', background: '#E4EDF8', borderRadius: '5px', overflow: 'hidden' },
  linkWrap: { background: '#F0F5FB', border: '1px solid #D9E5F4', borderRadius: '10px', padding: '11px 13px', margin: '0 0 14px' },
  linkHead: { fontSize: '11px', fontWeight: 700, color: '#154D97', marginBottom: '8px' },
  linkRow: { display: 'flex', gap: '7px' },
  linkInput: { flex: 1, padding: '7px 9px', border: '1px solid #C9D9EE', borderRadius: '8px', fontSize: '11.5px', color: '#5A6B82', fontFamily: 'inherit', background: '#fff' },
  linkBtn: { background: '#154D97', color: '#fff', border: 'none', borderRadius: '8px', padding: '7px 14px', fontSize: '12px', fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' },
  linkSub: { fontSize: '10.5px', color: '#7a8aa3', marginTop: '7px', lineHeight: 1.4 },
  linkGen: { background: '#fff', color: '#154D97', border: '1px dashed #C9D9EE', borderRadius: '8px', padding: '9px 14px', fontSize: '12.5px', fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', width: '100%' },
  listScroll: { maxHeight: '70vh', overflowY: 'auto' },
  lItem: (active) => ({ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', textAlign: 'left', border: 'none', borderBottom: '1px solid #EEF1F5', background: active ? '#E4EDF8' : 'transparent', padding: '9px 11px', cursor: 'pointer', fontFamily: 'inherit' }),
  lName: (active) => ({ fontSize: '12.5px', fontWeight: active ? 600 : 400, color: active ? '#154D97' : '#33415A' }),
  lMeta: { display: 'flex', alignItems: 'center', gap: '6px' },
  pCount: { fontSize: '11px', color: '#8a9ab5' },
  addLender: { display: 'flex', alignItems: 'center', gap: '6px', width: '100%', border: 'none', background: 'transparent', padding: '10px 11px', cursor: 'pointer', fontFamily: 'inherit', color: '#154D97', fontSize: '12px', fontWeight: 600 },

  detail: { flex: 1, minWidth: 0 },
  saveBar: (dirty) => ({ position: 'sticky', top: '12px', zIndex: 20, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px', background: dirty ? '#154D97' : '#F0F5FB', border: '1px solid #D9E5F4', borderRadius: '10px', padding: '10px 14px', marginBottom: '12px', transition: 'background 0.15s' }),
  saveBarText: (dirty) => ({ fontSize: '13px', fontWeight: 600, color: dirty ? '#fff' : '#8a9ab5' }),
  saveBtn: (dirty) => ({ background: dirty ? '#fff' : 'transparent', color: dirty ? '#154D97' : '#8A9AB5', border: 'none', borderRadius: '8px', padding: '7px 16px', fontSize: '12.5px', fontWeight: 700, cursor: dirty ? 'pointer' : 'default', fontFamily: 'inherit' }),

  nameRow: { display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '12px', flexWrap: 'wrap' },
  nameH: { fontSize: '16px', fontWeight: 700, color: '#2E4763' },
  vTag: (on) => ({ fontSize: '10px', fontWeight: 700, padding: '2px 8px', borderRadius: '10px', background: on ? '#EAF3DE' : '#F0F5FB', color: on ? '#3B6D11' : '#1A5FA8', textTransform: 'uppercase', letterSpacing: '0.04em' }),

  sectionBar: { background: '#E4EDF8', color: '#154D97', fontSize: '11px', fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', padding: '6px 12px', borderRadius: '5px', margin: '18px 0 10px' },
  secCard: { background: '#fff', borderRadius: '12px', border: '1px solid #C9D9EE', overflow: 'hidden', marginBottom: '14px', boxShadow: '0 1px 3px rgba(20,60,120,0.06)' },
  secHead: { background: '#D0E0F5', padding: '9px 16px', display: 'flex', alignItems: 'center', gap: '8px', borderBottom: '1px solid #BCD2EC' },
  secHeadIcon: { color: '#154D97', fontSize: '14px' },
  secTitle: { color: '#154D97', fontSize: '11.5px', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase' },
  secBody: { padding: '16px' },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '12px' },
  grid3: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '12px' },
  f: { display: 'flex', flexDirection: 'column', gap: '3px' },
  fFull: { display: 'flex', flexDirection: 'column', gap: '3px', gridColumn: '1 / -1' },
  fLabel: { fontSize: '11px', fontWeight: 400, color: '#8A9AB5' },
  input: { padding: '8px 10px', border: '1px solid #C9D9EE', borderRadius: '8px', fontSize: '13px', fontFamily: 'inherit', color: '#2E4763', width: '100%', background: '#fff', boxSizing: 'border-box', outline: 'none' },
  area: { padding: '8px 10px', border: '1px solid #C9D9EE', borderRadius: '8px', fontSize: '13px', fontFamily: 'inherit', color: '#2E4763', width: '100%', background: '#fff', boxSizing: 'border-box', outline: 'none', resize: 'vertical', minHeight: '60px' },
  chk: { display: 'inline-flex', width: 'fit-content', alignItems: 'center', gap: '7px', fontSize: '12.5px', cursor: 'pointer', userSelect: 'none', color: '#33415A' },
  flagsRow: { margin: '12px 0', display: 'flex', gap: '24px', flexWrap: 'wrap' },

  prog: (inactive) => ({ border: '1px solid #EEF1F5', borderRadius: '10px', marginBottom: '10px', overflow: 'hidden', opacity: inactive ? 0.55 : 1 }),
  progHead: (open) => ({ background: open ? '#F0F5FB' : '#fff', padding: '10px 16px', display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer', borderBottom: '1px solid #EEF1F5' }),
  progChev: { fontSize: '14px', color: '#4A7FBF' },
  progLt: { fontWeight: 600, color: '#2E4763', fontSize: '12.5px' },
  progBody: { padding: '16px', background: '#F6F9FC' },
  blk: (on) => ({ border: '1px solid ' + (on ? '#2B72C2' : '#C9D9EE'), borderRadius: '9px', overflow: 'hidden', background: '#fff', display: 'flex', flexDirection: 'column' }),
  blkHead: (on) => ({ background: on ? '#D6E8F8' : '#E4EDF8', borderBottom: '1px solid ' + (on ? '#9CC4E8' : '#B8D4F0'), padding: '6px 12px', display: 'flex', alignItems: 'center', gap: '8px' }),
  blkTitle: (on) => ({ color: on ? '#1A5FA8' : '#4A88C8', fontSize: '10px', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase' }),
  blkCount: { fontSize: '10px', fontWeight: 600, color: '#B08968', background: '#FBF7F3', border: '1px solid #EFE2D4', borderRadius: '10px', padding: '1px 7px', whiteSpace: 'nowrap' },
  blkEdit: { marginLeft: 'auto', fontSize: '10.5px', fontWeight: 600, color: '#2B72C2', cursor: 'pointer' },
  blkBody: { flex: 1, padding: '7px 0 9px' },
  blkDel: { fontSize: '10.5px', fontWeight: 600, color: '#c0392b', cursor: 'pointer' },
  subHint2: { fontSize: '10px', fontWeight: 700, color: '#4A88C8', letterSpacing: '0.07em', textTransform: 'uppercase', marginBottom: '6px' },
  wideBtn: { width: '100%', padding: '7px 10px', borderRadius: '7px', border: '1.5px solid #B8D4F0', background: '#fff', color: '#2B72C2', fontWeight: 600, fontSize: '12px', cursor: 'pointer', fontFamily: 'inherit' },
  lnRow: { display: 'flex', gap: '10px', padding: '4px 12px', fontSize: '12.5px', lineHeight: 1.45, alignItems: 'flex-start' },
  lnK: { width: '132px', flexShrink: 0, fontSize: '10px', fontWeight: 700, color: '#4A88C8', letterSpacing: '0.07em', textTransform: 'uppercase', paddingTop: '3px' },
  lnV: { flex: 1, minWidth: 0, color: '#0D2545' },
  lnVna: { flex: 1, minWidth: 0, color: '#B08968', fontStyle: 'italic' },
  lnInput: { width: '100%', padding: '5px 8px', border: '1.5px solid #B8D4F0', borderRadius: '5px', fontSize: '12.5px', color: '#0D2545', fontFamily: 'inherit', outline: 'none', background: '#fff' },
  lnArea: { width: '100%', minHeight: '56px', padding: '6px 8px', border: '1.5px solid #B8D4F0', borderRadius: '5px', fontSize: '12.5px', color: '#0D2545', fontFamily: 'inherit', outline: 'none', resize: 'vertical' },
  lnChips: { display: 'flex', flexWrap: 'wrap', gap: '4px' },
  lnChip: (on) => ({ fontSize: '11px', fontWeight: on ? 600 : 400, padding: '2px 8px', borderRadius: '4px', cursor: 'pointer', background: on ? '#D6E8F8' : '#fff', border: '1px solid ' + (on ? '#9CC4E8' : '#E6EAF0'), color: on ? '#1A5FA8' : '#8A94A6' }),
  cols2: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px', alignItems: 'stretch' },
  askRow: { display: 'flex', gap: '10px' },
  askInput: { flex: 1, padding: '7px 10px', border: '1.5px solid #B8D4F0', borderRadius: '6px', fontSize: '13px', fontFamily: 'inherit', outline: 'none', color: '#0D2545' },
  subHint: { fontSize: '10.5px', fontWeight: 700, color: '#154D97', letterSpacing: '0.06em', textTransform: 'uppercase', margin: '14px -16px 8px', padding: '6px 16px', background: '#E4EDF8', display: 'flex', alignItems: 'center', gap: '8px' },
  statesBox: { display: 'flex', flexWrap: 'wrap', gap: '5px', maxHeight: '120px', overflowY: 'auto', border: '1px solid #C9D9EE', borderRadius: '8px', padding: '8px', background: '#fff' },
  st: (on) => ({ fontSize: '11px', padding: '3px 7px', borderRadius: '5px', cursor: 'pointer', userSelect: 'none', background: on ? '#E4EDF8' : '#fff', color: on ? '#154D97' : '#5A6B82', border: '1px solid ' + (on ? '#BCD2EC' : '#E6EAF0'), fontWeight: on ? 600 : 400 }),
  specGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: '6px', marginTop: '4px' },
  spec: (on) => ({ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', padding: '5px 8px', border: '1px solid ' + (on ? '#BCD2EC' : '#E6EAF0'), borderRadius: '7px', cursor: 'pointer', userSelect: 'none', background: on ? '#E4EDF8' : '#fff', fontWeight: on ? 600 : 400, color: on ? '#154D97' : '#33415A' }),
  pill: (on) => ({ display: 'inline-flex', alignItems: 'center', gap: '5px', fontSize: '12px', padding: '5px 11px', borderRadius: '999px', border: '1px solid ' + (on ? '#BCD2EC' : '#E6EAF0'), background: on ? '#E4EDF8' : '#fff', color: on ? '#154D97' : '#5A6B82', cursor: 'pointer', userSelect: 'none', margin: '0 6px 6px 0', lineHeight: 1.25, fontWeight: on ? 600 : 400 }),
  pillbox: { display: 'flex', flexWrap: 'wrap', marginTop: '4px' },
  matchTag: { fontSize: '9px', fontWeight: 700, color: '#154D97', background: '#fff', borderRadius: '5px', padding: '2px 7px', textTransform: 'uppercase', letterSpacing: '0.04em', marginLeft: '8px' },
  refTag: { fontSize: '9px', fontWeight: 700, color: '#5A6B82', background: '#fff', borderRadius: '5px', padding: '2px 7px', textTransform: 'uppercase', letterSpacing: '0.04em', marginLeft: '8px' },
  hintLine: { fontSize: '11px', color: '#8a9ab5', margin: '6px 0' },
  rowBtns: { display: 'flex', gap: '8px', marginTop: '10px' },
  btnGhost: { background: '#fff', color: '#154D97', border: '1px solid #C9D9EE', borderRadius: '8px', padding: '6px 12px', fontSize: '12px', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' },
  btnDanger: { background: '#fff', color: '#A23A2E', border: '1px solid #EBC7C2', borderRadius: '8px', padding: '6px 12px', fontSize: '11px', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' },
  addProg: { background: '#fff', color: '#154D97', border: '1px dashed #C9D9EE', borderRadius: '9px', padding: '7px 13px', fontSize: '12px', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' },
  banner: { background: '#FAEEDA', border: '1px solid #F0DDBA', color: '#8a5a12', fontSize: '12px', padding: '8px 12px', borderRadius: '7px', marginBottom: '14px' },
  msg: { fontSize: '12px', marginTop: '10px' },
  ok: { color: '#3B6D11' },
  err: { color: '#c0392b' },
  loadBox: { fontSize: '13px', color: '#8a9ab5', padding: '20px' },
  delWrap: { marginTop: '24px' },
  readerToggle: { display: 'inline-flex', alignItems: 'center', gap: '6px', background: '#fff', color: '#154D97', border: '1px solid #C9D9EE', borderRadius: '8px', padding: '8px 14px', fontSize: '12.5px', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', marginBottom: '14px' },
  readerCard: { border: '1px solid #D9E5F4', borderRadius: '10px', padding: '14px', marginBottom: '16px', background: '#F0F5FB' },
  drop: { display: 'block', border: '1px dashed #4A7FBF', borderRadius: '9px', background: '#fff', padding: '18px', textAlign: 'center', color: '#5a6b85', fontSize: '12.5px', cursor: 'pointer', boxSizing: 'border-box', width: '100%' },
  orLine: { textAlign: 'center', fontSize: '10px', color: '#8a9ab5', margin: '10px 0', textTransform: 'uppercase', letterSpacing: '0.08em' },
  pasteBox: { width: '100%', minHeight: '64px', border: '1px solid #C9D9EE', borderRadius: '8px', padding: '9px', fontSize: '13px', fontFamily: 'inherit', color: '#2E4763', background: '#fff', boxSizing: 'border-box', outline: 'none', resize: 'vertical' },
  readBtn: (on) => ({ background: on ? '#154D97' : '#C9D9EE', color: '#fff', border: 'none', borderRadius: '7px', padding: '9px 18px', fontSize: '13px', fontWeight: 700, cursor: on ? 'pointer' : 'default', fontFamily: 'inherit', marginTop: '10px' }),
  propPanel: { background: '#fff', border: '1px solid #C9D9EE', borderRadius: '9px', padding: '14px', marginTop: '14px' },
  propRow: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px 16px' },
  propLab: { fontSize: '10px', fontWeight: 700, color: '#7189B0', letterSpacing: '0.05em', textTransform: 'uppercase' },
  propVal: { fontSize: '13px', color: '#0D2545', borderBottom: '1px solid #EEF1F5', paddingBottom: '3px' },
  propCard: { border: '1px solid #C9D9EE', borderRadius: '8px', padding: '10px 12px', marginBottom: '8px' },
  flagBox: { background: '#FAEEDA', border: '1px solid #F0DDBA', color: '#8a5a12', fontSize: '12px', padding: '8px 10px', borderRadius: '7px', marginTop: '10px' },
  approveBtn: { background: '#3B6D11', color: '#fff', border: 'none', borderRadius: '7px', padding: '9px 18px', fontSize: '13px', fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' },

  reviewIntro: { fontSize: '12px', color: '#5a6b85', marginBottom: '10px', lineHeight: 1.5 },
  chipRow: { display: 'flex', gap: '8px', marginBottom: '12px' },
  chip: (kind) => ({ flex: 1, borderRadius: '8px', padding: '8px 10px', textAlign: 'center', background: kind === 'fill' ? '#EAF3DE' : kind === 'conflict' ? '#FAEEDA' : '#F0F5FB' }),
  chipNum: (kind) => ({ fontSize: '16px', fontWeight: 700, color: kind === 'fill' ? '#3B6D11' : kind === 'conflict' ? '#8a5a12' : '#1A5FA8' }),
  chipLab: (kind) => ({ fontSize: '9.5px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', color: kind === 'fill' ? '#3B6D11' : kind === 'conflict' ? '#8a5a12' : '#1A5FA8' }),
  groupHead: (kind) => ({ fontSize: '10.5px', fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase', margin: '14px 0 8px', color: kind === 'fill' ? '#3B6D11' : '#8a5a12' }),
  acceptAll: { fontSize: '11px', fontWeight: 700, color: '#154D97', background: '#E4EDF8', borderRadius: '6px', padding: '4px 10px', border: 'none', cursor: 'pointer', fontFamily: 'inherit' },
  fillRow: { border: '1px solid #bcd9b0', borderLeft: '3px solid #3B6D11', borderRadius: '0 8px 8px 0', background: '#FAFDF7', padding: '9px 12px', marginBottom: '6px', display: 'flex', alignItems: 'center', gap: '10px' },
  fillCheck: (on) => ({ width: '18px', height: '18px', borderRadius: '4px', background: on ? '#3B6D11' : '#fff', color: '#fff', border: '1px solid ' + (on ? '#3B6D11' : '#C9D9EE'), display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px', flexShrink: 0, cursor: 'pointer' }),
  itemLabel: { fontSize: '12px', fontWeight: 700, color: '#154D97' },
  itemSub: { fontSize: '11px', color: '#7a8aa3', marginTop: '2px' },
  confRow: { border: '1px solid #F0DDBA', borderLeft: '3px solid #EF9F27', borderRadius: '0 8px 8px 0', background: '#FAEEDA', padding: '10px 12px', marginBottom: '7px' },
  confVals: { display: 'flex', gap: '8px', margin: '6px 0 8px' },
  confCell: { flex: 1, background: '#fff', border: '1px solid #C9D9EE', borderRadius: '6px', padding: '5px 8px', fontSize: '11px', color: '#0D2545', wordBreak: 'break-word', maxHeight: '90px', overflow: 'auto' },
  confTag: { fontSize: '8.5px', fontWeight: 700, textTransform: 'uppercase', color: '#7a8aa3', marginBottom: '2px' },
  choiceRow: { display: 'flex', gap: '6px', flexWrap: 'wrap' },
  choice: (on) => ({ fontSize: '11px', fontWeight: 700, padding: '4px 11px', borderRadius: '6px', cursor: 'pointer', userSelect: 'none', background: on ? '#154D97' : '#fff', color: on ? '#fff' : '#5a6b85', border: '1px solid ' + (on ? '#154D97' : '#C9D9EE') }),
  protTag: { fontSize: '9px', fontWeight: 700, color: '#3B6D11', background: '#EAF3DE', borderRadius: '4px', padding: '1px 6px', marginLeft: '6px' },
  progBanner: (isNew) => ({ background: isNew ? '#FAEEDA' : '#F0F5FB', border: '1px solid ' + (isNew ? '#F0DDBA' : '#D9E5F4'), borderRadius: '9px', padding: '10px 12px', margin: '14px 0 8px', display: 'flex', alignItems: 'center', gap: '10px' }),
  progBannerIcon: (isNew) => ({ width: '22px', height: '22px', borderRadius: '6px', background: isNew ? '#FAEEDA' : '#EAF3DE', color: isNew ? '#8a5a12' : '#3B6D11', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }),
  cosmeticToggle: { fontSize: '10.5px', color: '#7a8aa3', padding: '6px 2px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px', background: 'none', border: 'none', fontFamily: 'inherit' },

  coliveBox: { marginTop: '10px', background: '#F0F5FB', border: '1px solid #D9E5F4', borderLeft: '3px solid #4A7FBF', borderRadius: '0 9px 9px 0', padding: '11px 13px' },
  coliveHint: { fontSize: '10px', fontWeight: 700, color: '#154D97', letterSpacing: '0.05em', textTransform: 'uppercase', marginBottom: '7px' },

  keyBox: { background: '#FAEEDA', border: '1px solid #F0DDBA', borderRadius: '9px', padding: '11px 13px', marginBottom: '12px' },
  keyLabel: { fontSize: '10px', fontWeight: 700, color: '#8a5a12', letterSpacing: '0.04em', textTransform: 'uppercase', marginBottom: '7px' },
  keySaved: { fontSize: '11px', color: '#3B6D11', background: '#F2F8EC', border: '1px solid #bcd9b0', borderRadius: '7px', padding: '6px 10px', marginBottom: '12px' },
  keyChange: { color: '#154D97', fontWeight: 700, cursor: 'pointer', textDecoration: 'underline' },
  headCard: { background: '#fff', border: '1px solid #C9D9EE', borderRadius: '12px', boxShadow: '0 1px 3px rgba(20,60,120,0.06)', padding: '14px 16px 16px', marginBottom: '14px' },
  progInactive: { fontSize: '9.5px', fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase', padding: '2px 7px', borderRadius: '6px', background: '#F0F5FB', color: '#8A9AB5' },
  progMets: { marginLeft: 'auto', display: 'flex', gap: '18px', alignItems: 'center' },
  progMet: { textAlign: 'right' },
  progMetK: { display: 'block', fontSize: '8.5px', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: '#7189B0' },
  progMetV: { display: 'block', fontSize: '12px', fontWeight: 600, color: '#154D97', whiteSpace: 'nowrap' },
};

const num = (v) => (v === 0 || v) && v !== '' ? v : '';
const fmtK = (v) => { const n = parseFloat(String(v).replace(/[^0-9.]/g, '')); if (!n) return '-'; return n >= 1000000 ? '$' + (n / 1000000).toFixed(n % 1000000 ? 1 : 0) + 'M' : n >= 1000 ? '$' + Math.round(n / 1000) + 'K' : '$' + n; };
const fmtRange = (a, b) => (!a && !b) ? '-' : fmtK(a) + ' - ' + fmtK(b);

const FIELD_LABELS = {
  website: 'Website', phone: 'Main phone', lenderEmail: 'Lender email', address: 'Street address', city: 'City', hqState: 'State', zip: 'Zip',
  entity: 'Entity vs individual', ownerOcc: 'Owner-occupied', lenderMinFico: 'Min FICO floor', closingSpeed: 'Closing speed',
  appraisalRequired: 'Appraisal required', appraisalType: 'Appraisal type', ruralAllowed: 'Rural allowed', maxAcreage: 'Max acreage',
  lenderNote: 'Catch-all lender note', brokerFriendly: 'Broker friendly', brokerComp: 'Broker comp', lenderPoints: 'Lender points',
  minRate: 'Min rate', maxRate: 'Max rate', prepaymentPenalty: 'Prepayment penalty', foreignNational: 'Foreign national',
  reservesRequired: 'Reserves required', wholesale: 'Wholesale', assignFeeBasis: 'Assignment fee basis', assignFeeAmount: 'Assignment fee amount',
  bankruptcySeasoning: 'Bankruptcy seasoning', foreclosureSeasoning: 'Foreclosure seasoning', lateMortgage: 'Late mortgage',
  aeName: 'AE name', aePhone: 'AE phone', aeEmail: 'AE email', aeTitle: 'AE title', aeQuality: 'AE quality',
  onboarding: 'Onboarding', submissionMethod: 'Submission method', submissionEmail: 'Submission email', portalUrl: 'Portal URL', submissionNotes: 'Submission notes',
  recourseType: 'Recourse type', personalGuarantee: 'Personal guarantee', crossCollateral: 'Cross-collateral', maxCltv: 'Max CLTV',
  ysp: 'YSP allowed', otherFees: 'Other fees', locationReq: 'Location requirements', minPropValue: 'Minimum property value',
  fullApplication: 'Full application', creditPull: 'Credit pull', backgroundCheck: 'Background check', companyBackground: 'Company background',
  documentsRequired: 'Documents / overlays', compPaidBy: 'Comp paid by', itin: 'ITIN', nonPermResident: 'Non-permanent resident', propertyTypes: 'Property types', willNotLendOn: 'Will not lend on', unitCaps: 'Unit caps', specialties: 'Asset classes', situations: 'Situations', lienPositions: 'Lien positions', states: 'States', coLivingTypes: 'Co-living types',
  minLoan: 'Min loan', maxLoan: 'Max loan', minFico: 'Min FICO', maxLtvPurchase: 'Max LTV purchase', ltvBasis: 'LTV basis',
  maxLtvRefi: 'Max LTV refi', maxLtc: 'Max LTC', ltcIncludes: 'LTC includes', maxArv: 'Max ARV', rehabDelivery: 'Rehab delivery',
  minDscr: 'Min DSCR', rateMin: 'Rate min', rateMax: 'Rate max', points: 'Points', term: 'Term', experienceMin: 'Experience min',
  maxCommercial: 'Max commercial', minUnits: 'Min units', programNote: 'Program note', propertyType: 'Property types', propertyNotes: 'Property notes',
};
const NOTE_LENDER_KEYS = ['lenderNote', 'documentsRequired', 'onboarding', 'submissionNotes'];
const PROGRAM_NOTE_KEYS = ['programNote', 'propertyNotes'];
const ARRAY_LENDER_KEYS = ['states', 'specialties', 'situations', 'lienPositions', 'coLivingTypes', 'propertyTypes'];
const LENDER_NOMERGE = ['name', 'verified', 'matrixReceived', 'confirmToken', 'confirmationStatus', 'confirmationDate'];

function mpEmpty(v) { if (v == null) return true; if (Array.isArray(v)) return v.length === 0; return String(v).trim() === ''; }
function mpNorm(v) { return String(Array.isArray(v) ? v.join(', ') : v).trim().toLowerCase(); }
function mpLoose(v) { return mpNorm(v).replace(/[^a-z0-9]/g, ''); }
function mpLabel(k) { return FIELD_LABELS[k] || k; }
function mpDisp(v) { if (v == null) return ''; return Array.isArray(v) ? v.join(', ') : String(v); }
function mpClassify(cur, prop, isNote) {
  if (mpEmpty(cur)) return { kind: 'fill', cosmetic: false };
  if (mpNorm(cur) === mpNorm(prop)) return { kind: 'same', cosmetic: false };
  if (!isNote) {
    const a = mpLoose(cur), b = mpLoose(prop);
    if (a && b && (a.includes(b) || b.includes(a))) return { kind: 'conflict', cosmetic: true };
  }
  return { kind: 'conflict', cosmetic: false };
}
function canonicalLoanType(sIn) {
  const t = String(sIn || '').toLowerCase();
  const map = [
    ['ground-up', 'Ground-Up Construction'], ['ground up', 'Ground-Up Construction'], ['construction', 'Ground-Up Construction'],
    ['fix', 'Fix & Flip'], ['flip', 'Fix & Flip'],
    ['dscr', 'DSCR'], ['rental', 'DSCR'],
    ['bridge', 'Bridge'],
    ['agricultur', 'Agricultural'],
    ['sba 7', 'SBA 7(a)'], ['7(a)', 'SBA 7(a)'], ['sba 504', 'SBA 504'], ['504', 'SBA 504'],
    ['working capital', 'Working capital / LOC'], ['line of credit', 'Working capital / LOC'], ['loc', 'Working capital / LOC'],
    ['portfolio', 'Portfolio / Blanket'], ['blanket', 'Portfolio / Blanket'],
    ['chattel', 'MH Chattel'], ['mh land', 'MH Land/Home'], ['manufactured', 'MH Land/Home'],
    ['mezz', 'Gap / Mezzanine'], ['gap', 'Gap / Mezzanine'],
    ['2nd', '2nd Mortgage'], ['second mortgage', '2nd Mortgage'],
    ['commercial', 'Commercial'],
    ['land', 'Land'],
  ];
  for (let i = 0; i < map.length; i++) { if (t.includes(map[i][0])) return map[i][1]; }
  return String(sIn || '').trim();
}
function mpJoinNotes(a, b) {
  const x = String(a == null ? '' : a).trim();
  const y = String(b == null ? '' : b).trim();
  if (!x) return y;
  if (!y) return x;
  return x + '\n\n' + y;
}
function mpApply(obj, it, dec) {
  if (it.kind === 'same') return;
  if (it.kind === 'fill') { if (dec !== 'skip') obj[it.key] = it.proposed; return; }
  if (it.isNote) {
    if (dec === 'replace') obj[it.key] = it.proposed;
    else if (dec === 'append') obj[it.key] = mpJoinNotes(obj[it.key], it.proposed);
    return;
  }
  if (dec === 'use') obj[it.key] = it.proposed;
}
function buildMergePlan(proposed, draft, LK, PK) {
  const pl = (proposed && proposed.lender) || {};
  const lenderItems = [];
  LK.forEach((k) => {
    if (LENDER_NOMERGE.indexOf(k) !== -1) return;
    const pv = pl[k];
    if (mpEmpty(pv)) return;
    if (k === 'states') {
      const cur = new Set(String(draft.states || '').split(',').map((x) => x.trim().toUpperCase()).filter(Boolean));
      const incoming = (Array.isArray(pv) ? pv : String(pv).split(',')).map((x) => String(x).trim().toUpperCase()).filter(Boolean);
      const newOnes = incoming.filter((x) => !cur.has(x));
      if (!newOnes.length) return;
      lenderItems.push({ key: k, label: 'States', kind: 'fill', cosmetic: false, isStates: true, isArray: true, isNote: false, current: '', proposed: newOnes });
      return;
    }
    const isNote = NOTE_LENDER_KEYS.indexOf(k) !== -1;
    const isArr = ARRAY_LENDER_KEYS.indexOf(k) !== -1;
    const c = mpClassify(draft[k], pv, isNote);
    lenderItems.push({ key: k, label: mpLabel(k), kind: c.kind, cosmetic: c.cosmetic, isArray: isArr, isNote: isNote, isStates: false, current: draft[k], proposed: pv });
  });
  const pPrograms = (proposed && Array.isArray(proposed.programs)) ? proposed.programs : [];
  const programs = pPrograms.map((pp, idx) => {
    const canon = canonicalLoanType(pp.loanType);
    const match = (draft.programs || []).find((x) => canonicalLoanType(x.loanType) === canon || mpNorm(x.loanType) === mpNorm(pp.loanType));
    const items = [];
    PK.forEach((k) => {
      if (k === 'active' || k === 'loanType') return;
      const pv = pp[k];
      if (mpEmpty(pv)) return;
      const cur = match ? match[k] : '';
      const isNote = PROGRAM_NOTE_KEYS.indexOf(k) !== -1;
      const isArr = k === 'propertyType';
      const c = mpClassify(cur, pv, isNote);
      items.push({ key: k, label: mpLabel(k), kind: c.kind, cosmetic: c.cosmetic, isArray: isArr, isNote: isNote, isStates: false, current: cur, proposed: pv });
    });
    return { key: 'p' + idx, rawLoanType: pp.loanType || '', loanType: canon, matchId: match ? match.id : null, isNew: !match, items: items };
  });
  return { lenderItems: lenderItems, programs: programs };
}
function defaultMergeDecisions(plan) {
  const d = {};
  plan.lenderItems.forEach((it) => { d['L:' + it.key] = it.kind === 'fill' ? 'use' : 'keep'; });
  plan.programs.forEach((p) => p.items.forEach((it) => { d['P:' + p.key + ':' + it.key] = it.kind === 'fill' ? 'use' : 'keep'; }));
  return d;
}

function imageFileToScaledB64(file, maxDim, quality) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      const scale = Math.min(1, maxDim / Math.max(img.width, img.height));
      const w = Math.max(1, Math.round(img.width * scale));
      const h = Math.max(1, Math.round(img.height * scale));
      const c = document.createElement('canvas');
      c.width = w; c.height = h;
      const ctx = c.getContext('2d');
      ctx.fillStyle = '#ffffff'; ctx.fillRect(0, 0, w, h);
      ctx.drawImage(img, 0, 0, w, h);
      URL.revokeObjectURL(url);
      resolve(c.toDataURL('image/jpeg', quality).split(',')[1]);
    };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('Could not read the image')); };
    img.src = url;
  });
}

const READER_LOAN_TYPES = ['Fix & Flip', 'Ground-Up Construction', 'Bridge Short-Term', 'DSCR Long-Term Rental', 'Land & Agricultural', '2nd Mortgage & Specialty', 'Gap / Mezzanine', 'Stabilized', 'Mixed-Use', 'Commercial', 'Other'];
const READER_SCHEMA = `{
  "lender": {
    "website": "string or null", "phone": "string or null", "lenderEmail": "string or null",
    "address": "string or null", "city": "string or null", "hqState": "2-letter state or null", "zip": "string or null",
    "brokerFriendly": "'Yes' / 'No' / 'N/A' / null", "brokerComp": "free text or null",
    "ysp": "'Yes' / 'No' / 'Case by case' / null", "otherFees": "free text or null", "locationReq": "free text or null", "minPropValue": "free text or null",
    "fullApplication": "'Yes' / 'No' / 'Case by case' / null", "creditPull": "'Soft' / 'Hard' / 'None' / null", "backgroundCheck": "'Yes' / 'No' / 'Case by case' / null", "companyBackground": "free text or null",
    "entity": "'Entity required' / 'Individual OK' / 'Both' / null", "ownerOcc": "'Yes' / 'No' / null",
    "lenderPoints": "free text or null", "lenderMinFico": "number or null",
    "minRate": "free text or null", "maxRate": "free text or null", "closingSpeed": "free text or null",
    "prepaymentPenalty": "'Yes' / 'No' / 'Case-by-case' / null", "foreignNational": "'Yes' / 'No' / 'Case-by-case' / null",
    "reservesRequired": "free text or null", "appraisalRequired": "'Yes' / 'No' / 'Case-by-case' / null",
    "appraisalType": "'Full' / 'Desktop' / 'Drive-by' / 'BPO' / 'AVM' / 'Other' / null",
    "ruralAllowed": "'Yes' / 'No' / 'Case-by-case' / null", "maxAcreage": "number or null",
    "bankruptcySeasoning": "free text or null", "foreclosureSeasoning": "free text or null", "lateMortgage": "free text or null",
    "states": "array of 2-letter codes, [] if not stated", "lenderNote": "string or null"
  },
  "programs": [
    {
      "loanType": "MUST be one of the allowed loan types", "propertyTypes": "comma-separated free text",
      "minLoan": "number or null", "maxLoan": "number or null", "minFico": "number or null",
      "maxLtvPurchase": "WHOLE percent or null", "ltvBasis": "'Purchase price' / 'As-is value' / 'As-completed value' / 'Lower of purchase-or-appraisal' / null",
      "maxLtvRefi": "WHOLE percent or null", "maxLtc": "WHOLE percent or null",
      "ltcIncludes": "'Purchase + rehab' / 'Purchase only' / 'All-in incl carry+closing' / null",
      "maxArv": "WHOLE percent or null", "minDscr": "number or null",
      "rateMin": "WHOLE percent or null", "rateMax": "WHOLE percent or null", "points": "free text or null",
      "term": "free text or null", "experienceMin": "number or null",
      "maxCommercial": "WHOLE percent or null", "minUnits": "number or null",
      "programNote": "tiers, refi leverage, prepay schedules, caps, and anything program-specific with no field above"
    }
  ],
  "flags": "array of short strings - anything not found or needing a human to confirm"
}`;
const LENDER_SYSTEM_PROMPT = [
  'You are extracting LENDER PROGRAM GUIDELINES for Crafted Capital Co., a private/hard money loan brokerage. You will be shown a lender\'s matrix, term sheet, rate sheet, program flyer, email, or screenshots. Extract structured data to populate the lender\'s record.',
  '',
  'OUTPUT CONTRACT (mandatory): Return ONLY a single JSON object. No prose, no markdown fences. Shape:',
  READER_SCHEMA,
  '',
  'ALLOWED loanType VALUES (map each program to the closest one):',
  READER_LOAN_TYPES.map((t) => '  - ' + t).join('\n'),
  '',
  'LENDER-LEVEL vs PROGRAM-LEVEL (critical):',
  '- Put a value at the LENDER level when it is the same across all of the lender\'s programs (e.g. "we do not finance rural properties", "all loans are business-purpose to LLCs", appraisal always required, a company-wide FICO floor).',
  '- Put a value at the PROGRAM level when it varies by loan type (LTV/LTC/ARV, rates, term, min FICO that differs per program, experience required).',
  '- A value may appear at BOTH levels: the lender-wide floor/range as the headline, the precise per-program number on the program. That is expected for FICO and rate.',
  '',
  'RULES:',
  '- Percents are WHOLE numbers: 75 means 75%, never 0.75.',
  '- Use null for anything not stated. NEVER invent values. A wrong number is worse than a blank.',
  '- Read EVERY page / all attachments. A single lender packet often contains several program sheets - capture one entry in "programs" per distinct loan program you find.',
  '- For tiered programs (e.g. leverage by experience or by FICO), capture the headline/best-case numbers in the fields and put the full tier breakdown in that program\'s "programNote".',
  '- Put refinance leverage, prepay schedules, commercial caps, seasoning nuances, and any per-program detail with no dedicated field into "programNote".',
  '- states = array of 2-letter codes. If the document says "40 states and DC" but lists them, extract the actual codes. If it only gives a count with no list, leave states as [] and add a flag.',
  '- If you genuinely cannot tell which loan type a program is, use "Other" and note it in "flags".',
  '- Do NOT use em dashes or en dashes anywhere in your output. Use hyphens, commas, or periods.',
  '- Put anything you could not find, or that looks ambiguous and should be confirmed on a call, into the top-level "flags" array.',
].join('\n');

function buildLenderUserMsg(pastedText, fileName) {
  if (pastedText && pastedText.trim()) {
    return 'The following is text the broker pasted (e.g. a lender email or copied web page). Extract lender + program data from it.\n\n--- Pasted content ---\n' + pastedText.trim() + '\n\nReturn only the JSON object per the system instructions. No fences. No prose. No markdown.';
  }
  return 'Read the attached lender document(s) and extract lender + program data.\n' + (fileName ? ('File name (informational, not authoritative): ' + fileName + '\n') : '') + '\nReturn only the JSON object per the system instructions. No fences. No prose. No markdown.';
}

function parseClaudeJson(text) {
  if (!text || typeof text !== 'string') throw new Error('Empty response from Claude');
  let cleaned = text.trim().replace(/^```(?:json)?\s*/i, '').replace(/```\s*$/i, '').trim();
  const first = cleaned.indexOf('{');
  const last = cleaned.lastIndexOf('}');
  if (first > 0 && last > first) cleaned = cleaned.slice(first, last + 1);
  return JSON.parse(cleaned);
}

function LoanTypeSelect({ value, onChange }) {
  return (
    <div style={s.f}>
      <label style={s.fLabel}>Loan type</label>
      <select style={s.input} value={value ?? ''} onChange={onChange}>
        <option value="">-</option>
        {LOAN_TYPE_GROUPS.map(([g, opts]) => (
          <optgroup key={g} label={g}>
            {opts.map((o) => <option key={o} value={o}>{o}</option>)}
          </optgroup>
        ))}
      </select>
    </div>
  );
}

function LineRow({ label, value, editing, control, opts, onText, onToggle, renderView }) {
  if (!editing) {
    const blank = !value && value !== '0';
    return (
      <div style={s.lnRow}>
        <div style={s.lnK}>{label}</div>
        <div style={blank ? s.lnVna : s.lnV}>{blank ? 'not confirmed' : (renderView ? renderView(value) : value)}</div>
      </div>
    );
  }
  let ctrl;
  if (control === 'chips') {
    ctrl = (
      <div style={s.lnChips}>
        {opts.map((o) => {
          const on = String(value || '').split(', ').filter(Boolean).includes(o);
          return <span key={o} style={s.lnChip(on)} onClick={() => onToggle(o)}>{o}</span>;
        })}
      </div>
    );
  } else if (control === 'select') {
    ctrl = <select style={s.lnInput} value={value || ''} onChange={(e) => onText(e.target.value)}>{opts.map((o) => <option key={o} value={o}>{o || '-'}</option>)}</select>;
  } else if (control === 'area') {
    ctrl = <textarea style={s.lnArea} value={value || ''} onChange={(e) => onText(e.target.value)} />;
  } else {
    ctrl = <input style={s.lnInput} value={value || ''} onChange={(e) => onText(e.target.value)} placeholder="not confirmed" />;
  }
  return <div style={s.lnRow}><div style={s.lnK}>{label}</div><div style={{ flex: 1, minWidth: 0 }}>{ctrl}</div></div>;
}

function Blk({ title, lines, editing, onEdit, onDone, onDelete }) {
  const missing = lines.filter((l) => !l.value && l.value !== '0').length;
  return (
    <div style={s.blk(editing)}>
      <div style={s.blkHead(editing)}>
        <span style={s.blkTitle(editing)}>{title}</span>
        {!editing && missing > 0 && <span style={s.blkCount}>{missing} to ask</span>}
        <span style={s.blkEdit} onClick={editing ? onDone : onEdit}>{editing ? 'Done' : 'Edit'}</span>
        {editing && onDelete && <span style={s.blkDel} onClick={onDelete}>Delete</span>}
      </div>
      <div style={s.blkBody}>
        {lines.map((l) => <LineRow key={l.label} {...l} editing={editing} />)}
      </div>
    </div>
  );
}

function Section({ title, icon, children }) {
  return (
    <div style={s.secCard}>
      <div style={s.secHead}>
        {icon && <i className={`ti ${icon}`} style={s.secHeadIcon} />}
        <span style={s.secTitle}>{title}</span>
      </div>
      <div style={s.secBody}>{children}</div>
    </div>
  );
}

const GATE_FIELDS = ['states','specialties','situations','ownerOcc','foreignNational','entity','wholesale','recourseType','lienPositions','appraisalRequired','personalGuarantee','crossCollateral'];
function gFilled(v) { if (Array.isArray(v)) return v.length > 0; return v != null && v !== ''; }
function lenderCompleteness(l) {
  if (!l) return 0;
  let filled = 0; const total = GATE_FIELDS.length + 1;
  GATE_FIELDS.forEach((k) => { if (gFilled(l[k])) filled++; });
  const hasProg = Array.isArray(l.programs) && l.programs.some((p) => p.minLoan != null || p.maxLoan != null);
  if (hasProg) filled++;
  return Math.round((filled / total) * 100);
}
function lenderStatus(l) {
  if (l && l.verified) return 'verified';
  return lenderCompleteness(l) >= 60 ? 'partial' : 'unconfirmed';
}
const STATUS_DOT = { verified: '#3B6D11', partial: '#EF9F27', unconfirmed: '#8A9AB5' };
const STATUS_LABEL = { verified: 'Verified', partial: 'Partial', unconfirmed: 'Unconfirmed' };

export default function Lenders({ role }) {
  const [lenders, setLenders] = useState([]);
  const [counts, setCounts] = useState({ lenderCount: 0, programCount: 0, verifiedCount: 0 });
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [msg, setMsg] = useState('');
  const [query, setQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selId, setSelId] = useState(null);
  const [draft, setDraft] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState(false);
  const [openProgs, setOpenProgs] = useState({});
  const deletedPrograms = useRef([]);

  const [readerOpen, setReaderOpen] = useState(false);
  const [readerMode, setReaderMode] = useState('file');   // 'file' | 'email' | 'notes'
  const [readerText, setReaderText] = useState('');
  const [readerFile, setReaderFile] = useState(null);
  const [reading, setReading] = useState(false);
  const [proposed, setProposed] = useState(null);
  const [mergePlan, setMergePlan] = useState(null);
  const [mergeDecisions, setMergeDecisions] = useState({});
  const [showCosmetic, setShowCosmetic] = useState(false);
  const [readerErr, setReaderErr] = useState('');
  const [dragging, setDragging] = useState(false);

  const [editBlk, setEditBlk] = useState(null);   // 'Terms and pricing' | 'contacts' | 'prog:0'
  const [editSec1, setEditSec1] = useState(false);
  const [editSec2, setEditSec2] = useState(false);
  const [askQ, setAskQ] = useState('');
  const [askA, setAskA] = useState('');
  const [asking, setAsking] = useState(false);
  const blockMissing = useMemo(() => {
    if (!draft) return 0;
    let n = 0;
    LENDER_BLOCKS.forEach(([, lines]) => lines.forEach(([, key]) => { if (!lineValue(draft, key)) n++; }));
    return n;
  }, [draft]);
  const emailToAe = useCallback(() => {
    if (!draft) return;
    const to = draft.aeEmail || draft.lenderEmail || '';
    const sub = encodeURIComponent('Confirm your programs for Crafted Capital');
    const body = encodeURIComponent('Hi ' + (String(draft.aeName || '').split(' ')[0] || 'there') + ',\n\nTwo minutes and it means we send you better fitting deals:\n\n' + confirmUrl(draft.confirmToken) + '\n\nThank you,\nJennifer');
    window.location.href = 'mailto:' + to + '?subject=' + sub + '&body=' + body;
  }, [draft]);
  const runAsk = useCallback(async () => {
    if (!draft || !askQ.trim()) return;
    setAsking(true); setAskA('');
    try {
      const res = await authFetch('/.netlify/functions/ask-lender', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ lenderId: draft.id, question: askQ }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error((data && data.error) || 'ask failed');
      setAskA(data.answer || 'No answer came back.');
    } catch (e) {
      setAskA('Could not answer: ' + ((e && e.message) ? e.message : 'unknown error'));
    } finally { setAsking(false); }
  }, [draft, askQ]);
  const setLA = useCallback((key, val) => { setDraft((d) => (d ? { ...d, [key]: val } : d)); setDirty(true); }, []);
  const toggleLA = useCallback((key, val) => {
    setDraft((d) => {
      if (!d) return d;
      const cur = Array.isArray(d[key]) ? d[key] : [];
      return { ...d, [key]: cur.includes(val) ? cur.filter((x) => x !== val) : cur.concat([val]) };
    });
    setDirty(true);
  }, []);
  const setPA = useCallback((pi, key, val) => {
    setDraft((d) => {
      if (!d) return d;
      const ps = d.programs.slice();
      ps[pi] = { ...ps[pi], [key]: val };
      return { ...d, programs: ps };
    });
    setDirty(true);
  }, []);
  const togglePA = useCallback((pi, key, val) => {
    setDraft((d) => {
      if (!d) return d;
      const ps = d.programs.slice();
      const cur = Array.isArray(ps[pi][key]) ? ps[pi][key] : [];
      ps[pi] = { ...ps[pi], [key]: cur.includes(val) ? cur.filter((x) => x !== val) : cur.concat([val]) };
      return { ...d, programs: ps };
    });
    setDirty(true);
  }, []);
  const fileInputRef = useRef(null);

  const load = useCallback(async (focusId) => {
    setErr('');
    try {
      const res = await authFetch('/.netlify/functions/list-lenders');
      const data = await res.json();
      if (data.error) throw new Error(data.error?.message || JSON.stringify(data.error));
      const list = data.lenders || [];
      setLenders(list);
      setCounts({ lenderCount: data.lenderCount, programCount: data.programCount, verifiedCount: data.verifiedCount });
      const pick = focusId || selId || (list[0] && list[0].id);
      if (pick) {
        const found = list.find((l) => l.id === pick) || list[0];
        setSelId(found.id);
        setDraft(JSON.parse(JSON.stringify(found)));
      }
    } catch (e) {
      setErr('Could not load lenders: ' + e.message);
    } finally {
      setLoading(false);
    }
  }, [selId]);

  useEffect(() => { load(); }, []); // eslint-disable-line

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    let list = lenders;
    if (statusFilter !== 'all') list = list.filter((l) => lenderStatus(l) === statusFilter);
    if (q) list = list.filter((l) => l.name.toLowerCase().includes(q));
    return list;
  }, [lenders, query, statusFilter]);
  const rollup = useMemo(() => {
    let v = 0, p = 0, u = 0;
    lenders.forEach((l) => { const st = lenderStatus(l); if (st === 'verified') v++; else if (st === 'partial') p++; else u++; });
    return { v, p, u, total: lenders.length };
  }, [lenders]);

  function selectLender(id) {
    if (id === selId) return;
    if (dirty && !window.confirm('You have unsaved changes. Discard them and switch lenders?')) return;
    const found = lenders.find((l) => l.id === id);
    setSelId(id);
    setDraft(JSON.parse(JSON.stringify(found)));
    setDirty(false); setMsg(''); setErr('');
    setOpenProgs({});
    deletedPrograms.current = [];
    setReaderOpen(false); setReaderText(''); setReaderFile(null); setProposed(null); setReaderErr('');
    setMergePlan(null); setMergeDecisions({}); setShowCosmetic(false);
  }

  function touch(mutator) {
    setDraft((d) => { const copy = JSON.parse(JSON.stringify(d)); mutator(copy); return copy; });
    setDirty(true); setMsg('');
  }

  const setL = (key) => (e) => { const v = e.target.value; touch((d) => { d[key] = v; }); };
  const setLBool = (key) => (e) => { const v = e.target.checked; touch((d) => { d[key] = v; }); };
  const setP = (pi, key) => (e) => { const v = e.target.value; touch((d) => { d.programs[pi][key] = v; }); };
  const setPBool = (pi, key) => (e) => { const v = e.target.checked; touch((d) => { d.programs[pi][key] = v; }); };

  function toggleLenderState(st) {
    touch((d) => {
      const cur = String(d.states || '').split(',').map((x) => x.trim()).filter(Boolean);
      const set = new Set(cur);
      if (set.has(st)) set.delete(st); else set.add(st);
      d.states = [...set].join(', ');
    });
  }
  function selectAllStates() { touch((d) => { d.states = STATES.join(', '); }); }
  function clearAllStates() { touch((d) => { d.states = ''; }); }
  function toggleSituation(sit) {
    touch((d) => {
      const arr = Array.isArray(d.situations) ? d.situations : [];
      const set = new Set(arr);
      if (set.has(sit)) set.delete(sit); else set.add(sit);
      d.situations = [...set];
    });
  }
  function toggleLenderSpecialty(sp) {
    touch((d) => {
      const arr = Array.isArray(d.specialties) ? d.specialties : [];
      const set = new Set(arr);
      if (set.has(sp)) set.delete(sp); else set.add(sp);
      d.specialties = [...set];
    });
  }
  function toggleCoLivingType(ct) {
    touch((d) => {
      const arr = Array.isArray(d.coLivingTypes) ? d.coLivingTypes : [];
      const set = new Set(arr);
      if (set.has(ct)) set.delete(ct); else set.add(ct);
      d.coLivingTypes = [...set];
    });
  }
  function toggleLienPosition(lp) {
    touch((d) => {
      const arr = Array.isArray(d.lienPositions) ? d.lienPositions : [];
      const set = new Set(arr);
      if (set.has(lp)) set.delete(lp); else set.add(lp);
      d.lienPositions = [...set];
    });
  }
  function toggleProgramPropType(pi, pt) {
    touch((d) => {
      const arr = Array.isArray(d.programs[pi].propertyType) ? d.programs[pi].propertyType : [];
      const set = new Set(arr);
      if (set.has(pt)) set.delete(pt); else set.add(pt);
      d.programs[pi].propertyType = [...set];
    });
  }

  function newProgram(loanType) {
    return {
      id: null, loanType: loanType || '', propertyType: [], propertyNotes: '',
      minLoan: '', maxLoan: '', minFico: '', maxLtvPurchase: '', ltvBasis: '', maxLtvRefi: '',
      maxLtc: '', ltcIncludes: '', maxArv: '', rehabDelivery: '', minDscr: '', rateMin: '', rateMax: '',
      points: '', term: '', interestOnly: '', maxLtp: '', foreignNational: '', experienceMin: '', prepayPenalty: '', closingSpeed: '',
      reserves: '', maxCommercial: '', minUnits: '', programNote: '', active: true,
    };
  }

  function addProgram() {
    touch((d) => { d.programs.push(newProgram()); });
    setOpenProgs((o) => ({ ...o, [draft.programs.length]: true }));
  }
  function deleteProgram(pi) {
    if (!window.confirm('Delete this program?')) return;
    touch((d) => {
      const p = d.programs[pi];
      if (p.id) deletedPrograms.current.push(p.id);
      d.programs.splice(pi, 1);
    });
    setOpenProgs({});
  }

  async function addLender() {
    const name = window.prompt('New lender name:');
    if (!name || !name.trim()) return;
    setBusy(true); setErr(''); setMsg('');
    try {
      const res = await authFetch('/.netlify/functions/create-lender', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ fields: { name: name.trim() } }),
      });
      const data = await res.json();
      if (data.error) throw new Error(data.error?.message || JSON.stringify(data.error));
      setQuery('');
      await load(data.id);
      setMsg('Lender added - fill in details and Save.');
    } catch (e) { setErr('Could not add lender: ' + e.message); }
    finally { setBusy(false); }
  }

  async function deleteLender() {
    if (!draft) return;
    if (!window.confirm('Delete ' + draft.name + ' and all its programs? This cannot be undone.')) return;
    setBusy(true); setErr('');
    try {
      for (const p of draft.programs) {
        if (p.id) await authFetch('/.netlify/functions/update-program', {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ programId: p.id, _delete: true }),
        });
      }
      await authFetch('/.netlify/functions/update-lender', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ lenderId: draft.id, _delete: true }),
      });
      setSelId(null); setDraft(null); setDirty(false);
      await load();
    } catch (e) { setErr('Delete failed: ' + e.message); }
    finally { setBusy(false); }
  }

  function acceptFile(f) {
    if (!f) return;
    setReaderFile(f);
    setReaderErr('');
    setMsg('Selected: ' + f.name + ' - now click Read & propose.');
  }

  function onReaderFile(e) {
    const f = e.target.files && e.target.files[0];
    acceptFile(f);
    e.target.value = '';
  }

  function onReaderDrop(e) {
    e.preventDefault();
    setDragging(false);
    const f = e.dataTransfer && e.dataTransfer.files && e.dataTransfer.files[0];
    acceptFile(f);
  }

  function fileToBase64(file) {
    return new Promise((resolve, reject) => {
      const r = new FileReader();
      r.onload = () => resolve(String(r.result).split(',')[1]);
      r.onerror = () => reject(new Error('Could not read the file'));
      r.readAsDataURL(file);
    });
  }

  async function runReader() {
    if (reading) return;
    const hasText = readerText.trim().length > 0;
    if (readerMode === 'file' && !readerFile) { setReaderErr('Choose a file first.'); return; }
    if (readerMode !== 'file' && !hasText) { setReaderErr('Type or paste something first.'); return; }
    setReading(true); setReaderErr(''); setProposed(null);
    try {
      const name = readerFile ? (readerFile.name || '').toLowerCase() : '';
      const type = readerFile ? (readerFile.type || '') : '';
      const isXlsx = /\.(xlsx|xls)$/.test(name) || type.indexOf('spreadsheet') !== -1 || type.indexOf('ms-excel') !== -1;
      const isDocx = /\.(docx|doc)$/.test(name) || type.indexOf('wordprocessing') !== -1 || type === 'application/msword';

      // Excel / Word: small and fast, keep using the existing server parser.
      if (!hasText && (isXlsx || isDocx)) {
        const b64 = await fileToBase64(readerFile);
        const r = await authFetch('/.netlify/functions/extract-lender-doc', {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ fileBase64: b64, fileMimeType: readerFile.type, fileName: readerFile.name }),
        });
        const raw = await r.text();
        let data;
        try { data = JSON.parse(raw); } catch (pe) { throw new Error('The Excel or Word reader returned an unexpected response. Paste the text instead.'); }
        if (data.error) throw new Error(data.error?.message || String(data.error));
        setProposed(data.proposed);
        setReading(false);
        return;
      }

      // Text, PDF, image: read through the portal's own server. No time limit.
      const payload = { system: LENDER_SYSTEM_PROMPT };
      if (hasText) {
        payload.userMessage = buildLenderUserMsg(readerText, null);
      } else {
        const isImg = type.indexOf('image/') === 0 || /\.(jpe?g|png|webp|gif|bmp)$/.test(name);
        if (isImg) {
          payload.fileBase64 = await imageFileToScaledB64(readerFile, 1800, 0.8);
          payload.fileMimeType = 'image/jpeg';
        } else {
          payload.fileBase64 = await fileToBase64(readerFile);
          payload.fileMimeType = 'application/pdf';
        }
        payload.userMessage = buildLenderUserMsg(null, readerFile.name);
      }

      const res = await authFetch('/api/read-lender', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        let em = 'Reader error ' + res.status;
        try { const ej = await res.json(); if (ej && ej.error) em = ej.error; } catch (e2) { /* ignore */ }
        throw new Error(em);
      }
      let full = '';
      if (res.body && res.body.getReader) {
        const rd = res.body.getReader();
        const dec = new TextDecoder();
        while (true) { const { done, value } = await rd.read(); if (done) break; full += dec.decode(value, { stream: true }); }
        full += dec.decode();
      } else {
        full = await res.text();
      }
      let parsed;
      try { parsed = parseClaudeJson(full); } catch (pe) { throw new Error('Claude read the document but the result was not clean JSON. Try again, or paste the text.'); }
      const proposedObj = {
        lender: (parsed && parsed.lender && typeof parsed.lender === 'object') ? parsed.lender : {},
        programs: Array.isArray(parsed && parsed.programs) ? parsed.programs : [],
        flags: Array.isArray(parsed && parsed.flags) ? parsed.flags.map((x) => String(x).trim()).filter(Boolean) : [],
      };
      setProposed(proposedObj);
      setReading(false);
      return;
    } catch (e) {
      setReaderErr('Read failed: ' + ((e && e.message) ? e.message : 'unknown error'));
      setReading(false);
    }
  }

  function setMergeDec(id, val) { setMergeDecisions((o) => ({ ...o, [id]: val })); }

  function acceptAllFills() {
    if (!mergePlan) return;
    setMergeDecisions((o) => {
      const n = { ...o };
      mergePlan.lenderItems.forEach((it) => { if (it.kind === 'fill') n['L:' + it.key] = 'use'; });
      mergePlan.programs.forEach((p) => p.items.forEach((it) => { if (it.kind === 'fill') n['P:' + p.key + ':' + it.key] = 'use'; }));
      return n;
    });
  }

  function applyMergePlan() {
    if (!mergePlan) return;
    touch((d) => {
      mergePlan.lenderItems.forEach((it) => {
        const dec = mergeDecisions['L:' + it.key] || (it.kind === 'fill' ? 'use' : 'keep');
        if (it.isStates) {
          if (dec !== 'skip') {
            const cur = String(d.states || '').split(',').map((x) => x.trim()).filter(Boolean);
            const set = new Set(cur);
            (Array.isArray(it.proposed) ? it.proposed : []).forEach((st) => { const t = String(st).trim().toUpperCase(); if (t) set.add(t); });
            d.states = [...set].join(', ');
          }
          return;
        }
        mpApply(d, it, dec);
      });
      mergePlan.programs.forEach((p) => {
        let target = p.matchId ? (d.programs || []).find((x) => x.id === p.matchId) : null;
        if (!target) target = (d.programs || []).find((x) => canonicalLoanType(x.loanType) === p.loanType);
        if (!target) { target = newProgram(p.loanType); d.programs.push(target); }
        p.items.forEach((it) => {
          const dec = mergeDecisions['P:' + p.key + ':' + it.key] || (it.kind === 'fill' ? 'use' : 'keep');
          mpApply(target, it, dec);
        });
      });
    });
    setProposed(null); setMergePlan(null); setMergeDecisions({}); setShowCosmetic(false);
    setReaderOpen(false); setReaderText(''); setReaderFile(null);
    setMsg('Changes merged in. Review the fields, then Save all changes.');
  }

  const mergeCounts = useMemo(() => {
    if (!mergePlan) return { fills: 0, conflicts: 0, same: 0, cosmetic: 0 };
    let fills = 0, conflicts = 0, same = 0, cosmetic = 0;
    const all = mergePlan.lenderItems.concat(mergePlan.programs.reduce((a, p) => a.concat(p.items), []));
    all.forEach((it) => {
      if (it.kind === 'fill') fills++;
      else if (it.kind === 'same') same++;
      else if (it.cosmetic) cosmetic++;
      else conflicts++;
    });
    return { fills: fills, conflicts: conflicts, same: same, cosmetic: cosmetic };
  }, [mergePlan]);

  function renderMergeChoices(id, it) {
    const dec = mergeDecisions[id] || 'keep';
    const opt = (val, label) => <span key={val} style={s.choice(dec === val)} onClick={() => setMergeDec(id, val)}>{label}</span>;
    if (it.isNote) return <div style={s.choiceRow}>{opt('keep', 'Keep current')}{opt('replace', 'Replace')}{opt('append', 'Append both')}</div>;
    return <div style={s.choiceRow}>{opt('keep', 'Keep current')}{opt('use', 'Use document')}</div>;
  }

  function renderMergeItem(prefix, it) {
    const id = prefix + it.key;
    if (it.kind === 'fill') {
      const dec = mergeDecisions[id] || 'use';
      return (
        <div key={id} style={s.fillRow}>
          <span style={s.fillCheck(dec === 'use')} onClick={() => setMergeDec(id, dec === 'use' ? 'skip' : 'use')}>{dec === 'use' && <i className="ti ti-check" />}</span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={s.itemLabel}>{it.label}</div>
            <div style={s.itemSub}><span style={{ color: '#b0bdd0' }}>{it.isStates ? 'add to footprint' : 'empty'}</span> &#8594; <b style={{ color: '#3B6D11' }}>{mpDisp(it.proposed)}</b></div>
          </div>
        </div>
      );
    }
    return (
      <div key={id} style={s.confRow}>
        <div style={s.itemLabel}>{it.label}{it.isNote && <span style={s.protTag}><i className="ti ti-lock" style={{ fontSize: '10px' }} /> protected</span>}</div>
        <div style={s.confVals}>
          <div style={s.confCell}><div style={s.confTag}>current</div>{mpDisp(it.current)}</div>
          <div style={s.confCell}><div style={s.confTag}>document</div>{mpDisp(it.proposed)}</div>
        </div>
        {renderMergeChoices(id, it)}
      </div>
    );
  }

  const LENDER_KEYS = ['name','website','phone','brokerFriendly','brokerComp','entity','ownerOcc','wholesale','assignFeeBasis','assignFeeAmount','bankruptcySeasoning','foreclosureSeasoning','lateMortgage','situations','specialties','coLivingTypes','states','aeName','aePhone','aeEmail','aeTitle','aeQuality','onboarding','submissionMethod','submissionEmail','portalUrl','submissionNotes','matrixReceived','lenderNote','verified','lenderPoints','closingSpeed','prepaymentPenalty','foreignNational','reservesRequired','appraisalRequired','ruralAllowed','maxAcreage','appraisalType','lenderEmail','lenderMinFico','minRate','maxRate','address','city','hqState','zip','lienPositions','maxCltv','recourseType','personalGuarantee','crossCollateral','documentsRequired','confirmToken','confirmationStatus','confirmationDate','compPaidBy','itin','nonPermResident','propertyTypes','willNotLendOn','unitCaps','ysp','otherFees','locationReq','minPropValue','fullApplication','creditPull','backgroundCheck','companyBackground'];
  const PROGRAM_KEYS = ['loanType','propertyType','propertyNotes','minLoan','maxLoan','minFico','maxLtvPurchase','ltvBasis','maxLtvRefi','maxLtc','maxLtp','ltcIncludes','maxArv','rehabDelivery','minDscr','rateMin','rateMax','points','term','interestOnly','foreignNational','experienceMin','prepayPenalty','closingSpeed','reserves','maxCommercial','minUnits','programNote','active'];

  useEffect(() => {
    if (proposed && draft) {
      const pln = buildMergePlan(proposed, draft, LENDER_KEYS, PROGRAM_KEYS);
      setMergePlan(pln);
      setMergeDecisions(defaultMergeDecisions(pln));
      setShowCosmetic(false);
    } else {
      setMergePlan(null);
      setMergeDecisions({});
    }
  }, [proposed]); // eslint-disable-line

  function confirmUrl(tok) { return window.location.origin + '/confirm.html?token=' + tok; }
  function copyConfirm() {
    if (!draft || !draft.confirmToken) return;
    try { navigator.clipboard.writeText(confirmUrl(draft.confirmToken)); setMsg('Link copied'); setTimeout(() => setMsg(''), 1500); } catch (e) { /* ignore */ }
  }
  async function genLink() {
    if (!draft) return;
    const tok = 'cf' + Math.random().toString(36).slice(2, 10) + Math.random().toString(36).slice(2, 8);
    setBusy(true); setErr('');
    try {
      const res = await authFetch('/.netlify/functions/update-lender', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ lenderId: draft.id, fields: { confirmToken: tok } }) });
      if (!res.ok) throw new Error('Could not generate link');
      setDraft((d) => ({ ...d, confirmToken: tok }));
      setLenders((ls) => ls.map((l) => (l.id === draft.id ? { ...l, confirmToken: tok } : l)));
    } catch (e) { setErr(e.message || 'Could not generate link'); }
    finally { setBusy(false); }
  }

  async function saveAll() {
    if (!dirty || busy) return;
    setBusy(true); setErr(''); setMsg('');
    try {
      const lf = {};
      LENDER_KEYS.forEach((k) => { lf[k] = draft[k]; });
      const lr = await authFetch('/.netlify/functions/update-lender', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ lenderId: draft.id, fields: lf }),
      });
      const ld = await lr.json();
      if (ld.error) throw new Error(ld.error?.message || JSON.stringify(ld.error));

      for (const pid of deletedPrograms.current) {
        await authFetch('/.netlify/functions/update-program', {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ programId: pid, _delete: true }),
        });
      }
      deletedPrograms.current = [];

      for (const p of draft.programs) {
        const pf = {};
        PROGRAM_KEYS.forEach((k) => { pf[k] = p[k]; });
        if (p.id) {
          const r = await authFetch('/.netlify/functions/update-program', {
            method: 'POST', headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ programId: p.id, fields: pf }),
          });
          const d = await r.json();
          if (d.error) throw new Error(d.error?.message || JSON.stringify(d.error));
        } else {
          const r = await authFetch('/.netlify/functions/create-program', {
            method: 'POST', headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ lenderId: draft.id, fields: pf }),
          });
          const d = await r.json();
          if (d.error) throw new Error(d.error?.message || JSON.stringify(d.error));
        }
      }

      setDirty(false);
      await load(draft.id);
      setMsg('Saved.');
    } catch (e) {
      setErr('Save failed: ' + e.message + ' - your edits are still here; try Save again.');
    } finally {
      setBusy(false);
    }
  }

  if (loading) return <div style={s.loadBox}>Loading lenders…</div>;

  return (
    <div style={s.wrap}>
      <div style={s.listCard}>
        <div style={s.searchWrap}>
          <div style={s.searchBox}>
            <i className="ti ti-search" style={{ fontSize: '14px', color: '#8a9ab5' }} />
            <input style={s.searchInput} placeholder="Search lenders…" value={query} onChange={(e) => setQuery(e.target.value)} />
          </div>
        </div>
        <div style={s.rollWrap}>
          <div style={s.rollLine}>{rollup.v} verified · {rollup.p} partial · {rollup.u} unconfirmed</div>
          <div style={s.rollBar}>
            <div style={{ height: '100%', width: (rollup.total ? rollup.v / rollup.total * 100 : 0) + '%', background: '#3B6D11' }} />
            <div style={{ height: '100%', width: (rollup.total ? rollup.p / rollup.total * 100 : 0) + '%', background: '#EF9F27' }} />
          </div>
        </div>
        <div style={s.vfilter}>
          {[['all', 'All'], ['verified', 'Verified'], ['partial', 'Partial'], ['unconfirmed', 'Unconfirmed']].map(([k, lab]) => (
            <button key={k} style={s.vtab(statusFilter === k)} onClick={() => setStatusFilter(k)}>{lab}</button>
          ))}
        </div>
        <div style={s.listScroll}>
          {filtered.map((l) => (
            <button key={l.id} style={s.lItem(l.id === selId)} onClick={() => selectLender(l.id)}>
              <span style={s.lName(l.id === selId)}>{l.name}{l.id === selId && dirty ? ' •' : ''}</span>
              <span style={s.lMeta}>
                <span style={s.spct}>{lenderCompleteness(l)}%</span>
                <span style={s.sdot(STATUS_DOT[lenderStatus(l)])} />
                <span style={s.pCount}>{l.programs.length}</span>
              </span>
            </button>
          ))}
          <button style={s.addLender} onClick={addLender} disabled={busy}>
            <i className="ti ti-plus" style={{ fontSize: '14px' }} /> Add lender
          </button>
        </div>
      </div>

      <div style={s.detail}>
        {!draft ? (
          <div style={s.loadBox}>Select a lender on the left.</div>
        ) : (
          <>
            <div style={s.saveBar(dirty)}>
              <span style={s.saveBarText(dirty)}>
                {busy ? 'Saving…' : dirty ? 'Unsaved changes' : 'All changes saved'}
              </span>
              <button style={s.saveBtn(dirty)} onClick={saveAll} disabled={!dirty || busy}>
                {busy ? 'Saving…' : 'Save all changes'}
              </button>
            </div>

            <div style={s.headCard}>
            <div style={s.nameRow}>
              <span style={s.nameH}>{draft.name || '(unnamed lender)'}</span>
              <span style={s.statusPill(lenderStatus(draft))}>{STATUS_LABEL[lenderStatus(draft)]}</span>
              <span style={{ fontSize: '12px', color: '#8a9ab5' }}>{blockMissing} lines still to confirm</span>
            </div>
            {msg && <div style={{ ...s.msg, ...s.ok }}>{msg}</div>}
            {err && <div style={{ ...s.msg, ...s.err }}>{err}</div>}
            </div>

            <Section title="1 · Lender and contacts" icon="ti-building-bank">
            {!editSec1 && (
              <div>
                <div style={{ fontSize: '14px', fontWeight: 600 }}>{draft.name || 'Unnamed lender'}
                  <span style={{ fontWeight: 400, color: '#5B7396' }}>{(draft.hqState ? ' · ' + draft.hqState : '') + (draft.website ? ' · ' + draft.website : '') + (draft.phone ? ' · ' + draft.phone : '')}</span>
                </div>
                <div style={{ fontSize: '13px', marginTop: '6px' }}>
                  {draft.aeName ? <>AE: <b>{draft.aeName}</b>{draft.aePhone ? ' · ' + draft.aePhone : ''}{draft.aeEmail ? ' · ' + draft.aeEmail : ''}</> : <span style={{ color: '#B78A4A', fontStyle: 'italic' }}>No AE on file, call the main line for a name</span>}
                  {draft.submissionMethod ? <span style={{ color: '#5B7396' }}>{' · Submit: ' + draft.submissionMethod}</span> : null}
                </div>
                <div style={{ fontSize: '12px', color: '#5B7396', marginTop: '6px' }}>
                  {draft.companyBackground || ''}{draft.companyBackground ? ' · ' : ''}
                  {draft.verified ? <span style={{ color: '#27500A' }}>Verified</span> : <span style={{ color: '#B78A4A', fontStyle: 'italic' }}>Not verified yet</span>}
                  {draft.matrixReceived ? ' · Matrix on file' : ''}
                </div>
                <div style={{ marginTop: '8px' }}><button style={s.btnGhost} onClick={() => setEditSec1(true)}>Edit details</button></div>
              </div>
            )}
            {editSec1 && (<div>
            <div style={s.grid}>
              <Field label="Lender name" v={draft.name} on={setL('name')} />
              <Field label="Main phone" v={draft.phone} on={setL('phone')} />
              <Field label="Lender email" v={draft.lenderEmail} on={setL('lenderEmail')} />
              <Field label="Website" v={draft.website} on={setL('website')} />
              <Field label="AE name" v={draft.aeName} on={setL('aeName')} />
              <Field label="AE phone" v={draft.aePhone} on={setL('aePhone')} />
              <Field label="AE email" v={draft.aeEmail} on={setL('aeEmail')} />
              <Select label="Submission method" v={draft.submissionMethod} on={setL('submissionMethod')} opts={SUB_METHOD} />
              <Field label="Submission email" v={draft.submissionEmail} on={setL('submissionEmail')} />
              <Field label="Portal URL" v={draft.portalUrl} on={setL('portalUrl')} />
            </div>
            <div style={{ ...s.grid, marginTop: '10px' }}>
              <Field label="Submission notes" v={draft.submissionNotes} on={setL('submissionNotes')} ph="how they want it sent" />
              <Field label="Company background" v={draft.companyBackground} on={setL('companyBackground')} ph="founded, volume, licenses" />
            </div>
            <div style={s.flagsRow}>
              <label style={s.chk}><input type="checkbox" checked={!!draft.verified} onChange={setLBool('verified')} style={{ width: '16px', height: '16px' }} /> Verified</label>
              <label style={s.chk}><input type="checkbox" checked={!!draft.matrixReceived} onChange={setLBool('matrixReceived')} style={{ width: '16px', height: '16px' }} /> Matrix received</label>
            </div>
            <div style={{ marginTop: '10px' }}><button style={s.btnGhost} onClick={() => setEditSec1(false)}>Done</button></div>
            </div>)}
            </Section>

            <Section title="2 · Quick facts" icon="ti-map-2">
            {!editSec2 && (
              <div>
                {(() => {
                  const sts = String(draft.states || '').split(',').map((x) => x.trim()).filter(Boolean);
                  const sits = Array.isArray(draft.situations) ? draft.situations : [];
                  const specs = Array.isArray(draft.specialties) ? draft.specialties : [];
                  const stTxt = sts.length >= 45 ? ('Nationwide (' + sts.length + ')') : (sts.length ? sts.join(', ') : null);
                  const row = (label, txt, empty) => (
                    <div style={{ display: 'flex', padding: '4px 0', fontSize: '13px' }}>
                      <div style={{ width: '150px', flexShrink: 0, fontSize: '10px', fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', color: '#4A88C8', paddingTop: '3px' }}>{label}</div>
                      <div>{txt || <span style={{ color: '#B78A4A', fontStyle: 'italic' }}>{empty}</span>}</div>
                    </div>
                  );
                  return (
                    <div>
                      {row('States', stTxt, 'none confirmed')}
                      {row('Deal situations', sits.length ? sits.join(', ') : null, 'none confirmed')}
                      {row('Asset classes', specs.length ? specs.join(', ') : null, 'generalist, none confirmed')}
                    </div>
                  );
                })()}
                <div style={{ marginTop: '8px' }}><button style={s.btnGhost} onClick={() => setEditSec2(true)}>Edit: tap to toggle</button></div>
              </div>
            )}
            {editSec2 && (<div>
            <div style={s.subHint2}>States covered</div>
            <div style={{ display: 'flex', gap: '8px', marginBottom: '6px' }}>
              <button type="button" style={s.btnGhost} onClick={selectAllStates}>All states</button>
              <button type="button" style={s.btnGhost} onClick={clearAllStates}>Clear all</button>
            </div>
            <div style={s.statesBox}>
              {STATES.map((st) => {
                const on = String(draft.states || '').split(',').map((x) => x.trim()).includes(st);
                return <span key={st} style={s.st(on)} onClick={() => toggleLenderState(st)}>{st}</span>;
              })}
            </div>
            <div style={{ ...s.subHint2, marginTop: '14px' }}>Deal situations they accept</div>
            <div style={{ display: 'flex', gap: '8px', marginBottom: '6px' }}>
              <button type="button" style={s.btnGhost} onClick={() => setLA('situations', SITUATIONS.slice())}>All</button>
              <button type="button" style={s.btnGhost} onClick={() => setLA('situations', [])}>Clear all</button>
            </div>
            <div style={s.statesBox}>
              {SITUATIONS.map((v) => {
                const on = (Array.isArray(draft.situations) ? draft.situations : []).includes(v);
                return <span key={v} style={s.st(on)} onClick={() => toggleLA('situations', v)}>{v}</span>;
              })}
            </div>
            <div style={{ ...s.subHint2, marginTop: '14px' }}>Asset classes they fund (blank = generalist)</div>
            <div style={{ display: 'flex', gap: '8px', marginBottom: '6px' }}>
              <button type="button" style={s.btnGhost} onClick={() => setLA('specialties', ASSET_CLASSES.slice())}>All</button>
              <button type="button" style={s.btnGhost} onClick={() => setLA('specialties', [])}>Clear all</button>
            </div>
            <div style={s.statesBox}>
              {ASSET_CLASSES.map((v) => {
                const on = (Array.isArray(draft.specialties) ? draft.specialties : []).includes(v);
                return <span key={v} style={s.st(on)} onClick={() => toggleLA('specialties', v)}>{v}</span>;
              })}
            </div>
            <div style={{ marginTop: '10px' }}><button style={s.btnGhost} onClick={() => setEditSec2(false)}>Done</button></div>
            </div>)}
            </Section>

            <Section title="3 · Terms and requirements" icon="ti-list-details">
            <div style={s.cols2}>
              {LENDER_BLOCKS.map(([title, lines]) => (
                <Blk key={title} title={title} editing={editBlk === title}
                  onEdit={() => setEditBlk(title)} onDone={() => setEditBlk(null)}
                  lines={lines.map(([label, key, control, opts]) => ({
                    label, control, opts, value: lineValue(draft, key),
                    onText: (v) => setLA(key, v),
                    onToggle: (v) => toggleLA(key, v),
                  }))} />
              ))}
            </div>
            </Section>

            <Section title={`4 · Loan programs · ${draft.programs.length}`} icon="ti-stack-2">
            <div style={{ border: '1px solid #E4EDF8', borderRadius: '10px', overflow: 'hidden' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 2fr 1.3fr 1fr 0.7fr 1fr', gap: '0 10px', padding: '8px 12px', background: '#F0F5FB', fontSize: '10px', fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', color: '#5B7396' }}>
                <div>Type</div><div>Purchase</div><div>Loan</div><div>Rate</div><div>FICO</div><div>Term</div>
              </div>
              {draft.programs.map((p, pi) => (
                <div key={pi}>
                  <div onClick={() => setOpenProgs((o) => ({ ...o, [pi]: !o[pi] }))}
                    style={{ display: 'grid', gridTemplateColumns: '1.2fr 2fr 1.3fr 1fr 0.7fr 1fr', gap: '0 10px', padding: '9px 12px', borderTop: '1px solid #F0F5FB', fontSize: '12.5px', cursor: 'pointer', background: openProgs[pi] ? '#F9FBFE' : '#fff' }}>
                    <div style={{ color: '#154D97', fontWeight: 600 }}>{p.loanType || p.label || 'Program'}</div>
                    <div>{programLineValue(p, '__purchase') || <span style={{ color: '#B78A4A', fontStyle: 'italic' }}>not confirmed</span>}</div>
                    <div>{programLineValue(p, '__loansize') || <span style={{ color: '#B78A4A', fontStyle: 'italic' }}>not confirmed</span>}</div>
                    <div>{programLineValue(p, '__rate') || <span style={{ color: '#B78A4A', fontStyle: 'italic' }}>not confirmed</span>}</div>
                    <div>{programLineValue(p, 'minFico') || <span style={{ color: '#B78A4A', fontStyle: 'italic' }}>nc</span>}</div>
                    <div>{programLineValue(p, 'term') || <span style={{ color: '#B78A4A', fontStyle: 'italic' }}>nc</span>}</div>
                  </div>
                  {openProgs[pi] && (
                    <div style={{ padding: '4px 12px 12px', background: '#F9FBFE', borderTop: '1px solid #F0F5FB' }}>
                      <Blk title={p.loanType || p.label || 'Program'} editing={editBlk === 'prog:' + pi}
                        onEdit={() => setEditBlk('prog:' + pi)} onDone={() => setEditBlk(null)}
                        onDelete={() => { if (window.confirm('Delete the ' + (p.loanType || 'this') + ' program?')) { deleteProgram(pi); setEditBlk(null); } }}
                        lines={PROGRAM_LINES.map(([label, key, control, opts]) => ({
                          label: programLineLabel(p, label, key),
                          control, opts, value: programLineValue(p, key),
                          onText: (v) => setPA(pi, key, v),
                          onToggle: (v) => togglePA(pi, key, v),
                        }))} />
                    </div>
                  )}
                </div>
              ))}
            </div>
            <button style={s.btnGhost} onClick={addProgram}>+ Add a program</button>
            </Section>

            <Section title="5 · Ask about this lender" icon="ti-message-question">
            <div style={s.askRow}>
              <input style={s.askInput} value={askQ} onChange={(e) => setAskQ(e.target.value)}
                placeholder="720 FICO, $1.2M purchase, 3 acres in Texas, will they do it?" />
              <button style={s.saveBtn(true)} onClick={runAsk} disabled={asking}>{asking ? 'Reading...' : 'Ask'}</button>
            </div>
            {askA && <div style={{ ...s.msg, ...s.ok, marginTop: '10px', whiteSpace: 'pre-wrap' }}>{askA}</div>}
            </Section>

            <Section title="6 · Lender updates" icon="ti-refresh">
            <div style={s.subHint2}>AE self-serve link</div>
            {draft.confirmToken ? (
              <>
                <div style={s.linkRow}>
                  <input readOnly value={confirmUrl(draft.confirmToken)} style={s.linkInput} onFocus={(e) => e.target.select()} />
                  <button style={s.linkBtn} onClick={copyConfirm}>Copy</button>
                  <button style={s.linkBtn} onClick={emailToAe}>Email to AE</button>
                </div>
                <div style={s.hintLine}>{draft.confirmationStatus ? ('Submitted ' + (draft.confirmationDate || '')) : 'Not submitted yet.'}</div>
              </>
            ) : (
              <button style={s.linkGen} onClick={genLink} disabled={busy}>{busy ? 'Working...' : 'Generate the link'}</button>
            )}
            <div style={{ ...s.subHint2, marginTop: '16px' }}>Add what they sent you</div>
            <div style={s.grid3}>
              <button style={s.wideBtn} onClick={() => { setReaderMode('file'); setReaderOpen(true); setReaderErr(''); setReaderText(''); setReaderFile(null); }}>Upload a document</button>
              <button style={s.wideBtn} onClick={() => { setReaderMode('email'); setReaderOpen(true); setReaderErr(''); setReaderText(''); setReaderFile(null); }}>Paste an email</button>
              <button style={s.wideBtn} onClick={() => { setReaderMode('notes'); setReaderOpen(true); setReaderErr(''); setReaderText(''); setReaderFile(null); }}>Type call notes</button>
            </div>
            {!readerOpen ? null : (
              <div style={s.readerCard}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                  <span style={{ fontSize: '13px', fontWeight: 700, color: '#154D97' }}>{readerMode === 'file' ? 'Upload a document' : readerMode === 'email' ? 'Paste an email' : 'Type call notes'} · {draft.name}</span>
                  <button style={s.btnGhost} onClick={() => { setReaderOpen(false); setProposed(null); setReaderText(''); setReaderFile(null); setReaderErr(''); }}>Close</button>
                </div>


                {!proposed && (
                  <>
                    {readerMode === 'file' && (
                      <>
                        <input ref={fileInputRef} type="file" accept=".pdf,.jpg,.jpeg,.png,.webp,.xlsx,.xls,.docx,.doc" onChange={onReaderFile} style={{ display: 'none' }} />
                        <div
                          style={{ ...s.drop, ...(dragging ? { background: '#E4EDF8', borderColor: '#154D97' } : {}) }}
                          onClick={() => fileInputRef.current && fileInputRef.current.click()}
                          onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
                          onDragEnter={(e) => { e.preventDefault(); setDragging(true); }}
                          onDragLeave={(e) => { e.preventDefault(); setDragging(false); }}
                          onDrop={onReaderDrop}
                        >
                          <div style={{ fontSize: '20px', lineHeight: 1, marginBottom: '6px' }}><i className="ti ti-upload" /></div>
                          <div>{readerFile ? readerFile.name : (dragging ? 'Drop the file here' : 'Click to choose, or drag a file here')}</div>
                          <div style={{ fontSize: '11px', color: '#8a9ab5', marginTop: '4px' }}>Matrix, rate sheet, term sheet, flyer. PDF, Excel, Word, JPG, PNG. Multi-page reads fine.</div>
                        </div>
                      </>
                    )}
                    {readerMode !== 'file' && (
                      <textarea style={s.pasteBox}
                        placeholder={readerMode === 'email' ? "Paste the email they sent, or text off their website..." : "Type what they told you on the call. Plain sentences are fine, no format needed..."}
                        value={readerText} onChange={(e) => setReaderText(e.target.value)} />
                    )}
                    {readerErr && <div style={{ ...s.msg, ...s.err }}>{readerErr}</div>}
                    <button style={s.readBtn(!reading && (readerText.trim() || readerFile))} onClick={runReader} disabled={reading || (!readerText.trim() && !readerFile)}>
                      {reading ? 'Reading...' : (readerMode === 'file' ? 'Read this document' : readerMode === 'email' ? 'Read this email' : 'File these notes')}
                    </button>
                  </>
                )}

                {mergePlan && (() => {
                  const lenderFills = mergePlan.lenderItems.filter((it) => it.kind === 'fill');
                  const lenderConflicts = mergePlan.lenderItems.filter((it) => it.kind === 'conflict' && !it.cosmetic);
                  const cosmeticItems = [];
                  mergePlan.lenderItems.forEach((it) => { if (it.kind === 'conflict' && it.cosmetic) cosmeticItems.push({ prefix: 'L:', it: it }); });
                  mergePlan.programs.forEach((p) => p.items.forEach((it) => { if (it.kind === 'conflict' && it.cosmetic) cosmeticItems.push({ prefix: 'P:' + p.key + ':', it: it }); }));
                  return (
                    <div style={s.propPanel}>
                      <div style={s.reviewIntro}><i className="ti ti-circle-check" style={{ color: '#3B6D11' }} /> Review what the document changes. Fields it does not mention are left untouched. Nothing saves until you hit Save all changes.</div>

                      <div style={s.chipRow}>
                        <div style={s.chip('fill')}><div style={s.chipNum('fill')}>{mergeCounts.fills}</div><div style={s.chipLab('fill')}>fills</div></div>
                        <div style={s.chip('conflict')}><div style={s.chipNum('conflict')}>{mergeCounts.conflicts}</div><div style={s.chipLab('conflict')}>conflicts</div></div>
                        <div style={s.chip('same')}><div style={s.chipNum('same')}>{mergeCounts.same}</div><div style={s.chipLab('same')}>unchanged</div></div>
                      </div>

                      {lenderFills.length > 0 && (
                        <>
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <div style={s.groupHead('fill')}>Lender fills, blank fields</div>
                            <button style={s.acceptAll} onClick={acceptAllFills}>Accept all fills</button>
                          </div>
                          {lenderFills.map((it) => renderMergeItem('L:', it))}
                        </>
                      )}

                      {lenderConflicts.length > 0 && (
                        <>
                          <div style={s.groupHead('conflict')}>Lender conflicts</div>
                          {lenderConflicts.map((it) => renderMergeItem('L:', it))}
                        </>
                      )}

                      {mergePlan.programs.map((p) => {
                        const fills = p.items.filter((it) => it.kind === 'fill');
                        const confs = p.items.filter((it) => it.kind === 'conflict' && !it.cosmetic);
                        if (!fills.length && !confs.length && !p.isNew) return null;
                        return (
                          <div key={p.key}>
                            <div style={s.progBanner(p.isNew)}>
                              <span style={s.progBannerIcon(p.isNew)}><i className={p.isNew ? 'ti ti-plus' : 'ti ti-arrows-join'} /></span>
                              <div style={{ flex: 1 }}>
                                <div style={s.itemLabel}>{p.isNew ? ('Creates new program: ' + p.loanType) : ((p.rawLoanType || p.loanType) + ' updates your existing ' + p.loanType + ' program')}</div>
                                <div style={{ fontSize: '10.5px', color: '#5a6b85', marginTop: '2px' }}>{p.isNew ? 'No existing match for this loan type.' : 'Loan type normalized, so no duplicate is created.'}</div>
                              </div>
                            </div>
                            {fills.map((it) => renderMergeItem('P:' + p.key + ':', it))}
                            {confs.map((it) => renderMergeItem('P:' + p.key + ':', it))}
                          </div>
                        );
                      })}

                      {cosmeticItems.length > 0 && (
                        <>
                          <button style={s.cosmeticToggle} onClick={() => setShowCosmetic((v) => !v)}><i className={showCosmetic ? 'ti ti-chevron-down' : 'ti ti-chevron-right'} /> {cosmeticItems.length} formatting-only difference{cosmeticItems.length > 1 ? 's' : ''}{showCosmetic ? '' : ' hidden'}</button>
                          {showCosmetic && cosmeticItems.map((ci) => renderMergeItem(ci.prefix, ci.it))}
                        </>
                      )}

                      {proposed && (proposed.flags || []).length > 0 && (
                        <div style={s.flagBox}><i className="ti ti-alert-triangle" /> Confirm on a call: {proposed.flags.join('; ')}</div>
                      )}

                      <div style={{ display: 'flex', gap: '10px', marginTop: '14px' }}>
                        <button style={s.approveBtn} onClick={applyMergePlan}><i className="ti ti-check" /> Apply selected</button>
                        <button style={s.btnGhost} onClick={() => setProposed(null)}>Read something else</button>
                      </div>
                    </div>
                  );
                })()}
              </div>
            )}
            </Section>



            <div style={s.delWrap}>
              <button style={s.btnDanger} onClick={deleteLender} disabled={busy}>Delete this lender</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function Field({ label, v, on, ph }) {
  return (
    <div style={s.f}>
      {label ? <label style={s.fLabel}>{label}</label> : null}
      <input style={s.input} value={v ?? ''} onChange={on} placeholder={ph || ''} />
    </div>
  );
}
function Area({ label, v, on }) {
  return (
    <div style={s.fFull}>
      {label ? <label style={s.fLabel}>{label}</label> : null}
      <textarea style={s.area} value={v ?? ''} onChange={on} />
    </div>
  );
}
function Select({ label, v, on, opts }) {
  return (
    <div style={s.f}>
      <label style={s.fLabel}>{label}</label>
      <select style={s.input} value={v ?? ''} onChange={on}>
        {opts.map((o) => <option key={o} value={o}>{o || '-'}</option>)}
      </select>
    </div>
  );
}
